import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getLanguages } from '../api/workers'
import { useAuthStore } from '../store/authStore'

interface Language {
  code: string
  name: string
  native_name: string
  is_default: boolean
}

export default function CrowdLanguagePicker() {
  const { i18n } = useTranslation()
  const { setPreferredLanguage } = useAuthStore()
  const [languages, setLanguages] = useState<Language[]>([])
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    getLanguages().then(setLanguages).catch(() => {})
  }, [])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (languages.length <= 1) return null

  const currentCode = i18n.language
  const current = languages.find((l) => l.code === currentCode) ?? languages.find((l) => l.is_default) ?? languages[0]

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-10 h-10 flex items-center justify-center rounded-full"
        style={{ background: 'var(--app-card-bg)' }}
        aria-label="Select language"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--tg-theme-link-color)' }}>
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 rounded-2xl overflow-hidden shadow-lg z-50 min-w-[140px]"
          style={{ background: 'var(--app-card-bg)', border: '1px solid var(--tg-theme-hint-color)', opacity: 0.95 }}
        >
          {languages.map((lang) => {
            const isActive = lang.code === current?.code
            return (
              <button
                key={lang.code}
                onClick={() => {
                  setOpen(false)
                  if (!isActive) {
                    i18n.changeLanguage(lang.code)
                    setPreferredLanguage(lang.code)
                  }
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors"
                style={{
                  background: isActive ? 'var(--tg-theme-link-color)' : 'transparent',
                  color: isActive ? '#ffffff' : 'var(--tg-theme-text-color)',
                }}
              >
                <span className="font-mono text-xs font-bold uppercase opacity-70 w-6">
                  {lang.code}
                </span>
                <span className="font-medium">{lang.native_name}</span>
                {isActive && (
                  <svg className="ml-auto" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            )
          })}
          {current && (
            <div
              className="px-4 py-2 text-xs text-center"
              style={{ color: 'var(--tg-theme-hint-color)', borderTop: '1px solid var(--tg-theme-hint-color)', opacity: 0.6 }}
            >
              {current.name}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
